import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { fileUrl } from "../lib/api";

const SiteSettingsContext = createContext({ settings: null, refresh: () => {} });

/**
 * Provides global site settings (logo, hero copy, contact, social, SEO).
 * Loaded once on app mount with retry-safe axios interceptor.
 */
export function SiteSettingsProvider({ children }) {
    const [settings, setSettings] = useState(null);
    const loadedOnce = useRef(false);

    const load = (force = false) => {
        if (!force && loadedOnce.current) return;
        loadedOnce.current = true;
        api.get("/site-settings")
            .then((r) => setSettings(r.data))
            .catch(() => {
                loadedOnce.current = false;
                setSettings({});
            });
    };

    useEffect(() => { load(); }, []);

    // Apply favicon + SEO meta tags whenever settings change
    useEffect(() => {
        if (!settings) return;
        // Favicon
        if (settings.favicon_path) {
            const href = fileUrl(settings.favicon_path);
            let link = document.querySelector("link[rel~='icon']");
            if (!link) {
                link = document.createElement("link");
                link.rel = "icon";
                document.head.appendChild(link);
            }
            link.href = href;
        }
        // Document title + meta description (uses current document lang via <html lang>)
        const docLang = document.documentElement.lang || "ar";
        const title = (docLang === "ar" ? settings.seo_title_ar : settings.seo_title_en) || settings.seo_title_en || settings.seo_title_ar;
        const desc  = (docLang === "ar" ? settings.seo_description_ar : settings.seo_description_en) || settings.seo_description_en || settings.seo_description_ar;
        if (title) document.title = title;
        if (desc) {
            let m = document.querySelector("meta[name='description']");
            if (!m) { m = document.createElement("meta"); m.setAttribute("name", "description"); document.head.appendChild(m); }
            m.setAttribute("content", desc);
        }
        // OG image + canonical
        const setMeta = (selector, attr, value) => {
            if (!value) return;
            let el = document.querySelector(selector);
            if (!el) {
                el = document.createElement(selector.startsWith("link") ? "link" : "meta");
                if (selector.startsWith("link")) el.setAttribute("rel", "canonical");
                else el.setAttribute("property", selector.match(/\[property='([^']+)'\]/)?.[1] || "");
                document.head.appendChild(el);
            }
            el.setAttribute(attr, value);
        };
        if (settings.seo_og_image) {
            const ogUrl = fileUrl(settings.seo_og_image);
            setMeta("meta[property='og:image']", "content", ogUrl);
        }
        if (title) setMeta("meta[property='og:title']", "content", title);
        if (desc) setMeta("meta[property='og:description']", "content", desc);
        if (settings.seo_canonical_url) setMeta("link[rel='canonical']", "href", settings.seo_canonical_url);
    }, [settings]);

    return (
        <SiteSettingsContext.Provider value={{ settings: settings || {}, settingsLoading: settings === null, refresh: () => load(true) }}>
            {children}
        </SiteSettingsContext.Provider>
    );
}

export function useSiteSettings() {
    return useContext(SiteSettingsContext);
}
